import { currencyToSymbol } from '@/utilities/formatting';
import { Heading, Button } from '@simplywallst/ui-core/next';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';

import type { Holding } from '@/hooks/portfolio/usePortfolio';

import { TickerSearch } from '../TickerSearch';

import { TypeField } from '../TypeField';
import { TotalCostField } from '../TotalCostField';

import { NumericField } from '../../../NumericField';
import { DateField } from '../../../DateField';

import { SuccessAlert } from './SuccessAlert';
import { Error } from './Error';

import type {
  useAddTransactionToPortfolio,
  useTransactionForm,
} from '../../hooks';

interface Props {
  holding?: Holding;
  formState: ReturnType<typeof useTransactionForm>;
  mutationState: ReturnType<typeof useAddTransactionToPortfolio>;
  savedTicker?: string;
  onClose: () => void;
  onSave: () => void;
  onSaveAndAddMore: () => void;
}

export const TransactionForm = ({
  formState,
  mutationState,
  savedTicker,
  holding,
  onClose,
  onSave,
  onSaveAndAddMore,
}: Props) => {
  const { values, errors, update } = formState;
  const { isPending, isError, error, isSuccess } = mutationState;

  const currencySymbol: string | undefined =
    currencyToSymbol[formState.values.ticker?.currencyISO ?? '']?.currency
      .symbol;

  return (
    <div className="grid grid-rows-[1fr_max-content] content-between gap-8 pb-4 pt-6">
      <div className="grid content-between px-6">
        <div>
          <div className="grid grid-cols-[auto_40px] grid-rows-[minmax(40px,max-content)] items-center justify-between">
            <Heading as="h3" className="text-solid">
              Add transaction
            </Heading>
            <Button
              type="button"
              variant="subtle"
              onClick={onClose}
              aria-label="Close"
              data-cy-id="transaction-form-close"
              className="p-0"
            >
              <CrossSolid className="fill-p-white" />
            </Button>
          </div>
          <div className="grid grid-rows-[repeat(5,max-content)] gap-6">
            <TypeField
              value={values.type}
              onChange={(value) => update('type', value)}
            />
            <TickerSearch
              value={values.ticker}
              error={errors.ticker}
              isDisabled={typeof holding !== 'undefined'}
              onChange={(value) => update('ticker', value)}
            />
            <DateField
              label="Date (DD/MM/YYYY)"
              aria-label="Date"
              value={values.date}
              error={errors.date}
              onChange={(value) => {
                if (value === null) return update('date', null);
                const date = new Date();
                date.setFullYear(value.year, value.month - 1, value.day);
                value && update('date', date);
              }}
              lang="en-GB"
            />
            <div className="grid grid-cols-[1fr_1fr] gap-x2">
              <NumericField
                label="Number of Shares"
                aria-label="Number of Shares"
                decimalScale={9}
                value={values.shares}
                error={errors.shares}
                onValueChange={(value) => {
                  const { floatValue } = value;
                  const validatedValue =
                    typeof floatValue === 'undefined'
                      ? null
                      : Number.isNaN(floatValue ?? 0)
                        ? 0
                        : floatValue;
                  update('shares', validatedValue);
                }}
              />
              <NumericField
                label="Share Price"
                aria-label="Share Price"
                decimalScale={9}
                prefix={currencySymbol}
                value={values.price}
                error={errors.price}
                onValueChange={(value) => {
                  const { floatValue } = value;
                  const validatedValue =
                    typeof floatValue === 'undefined'
                      ? null
                      : Number.isNaN(floatValue ?? 0)
                        ? 0
                        : floatValue;
                  update('price', validatedValue);
                }}
              />
            </div>
            <TotalCostField
              shares={values.shares}
              price={values.price}
              currencyISO={values.ticker?.currencyISO ?? null}
              currencySymbol={currencySymbol}
            />
          </div>
        </div>
        {isError && values.ticker?.symbol && (
          <Error
            error={error}
            transaction={{ tickerSymbol: values.ticker.symbol }}
          />
        )}
        {isSuccess && savedTicker && <SuccessAlert ticker={savedTicker} />}
      </div>
      <div className="border-0 border-t border-solid border-subtle px-6 pt-4">
        <div className="grid grid-cols-[max-content_max-content] justify-end gap-4">
          <Button
            type="button"
            variant="secondary"
            isLoading={isPending}
            onClick={onSaveAndAddMore}
          >
            Save and add more
          </Button>
          <Button type="button" isLoading={isPending} onClick={onSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
